import { DataSource, TableRow } from "@mcleod/components";
import { ActionPermission, Api, ModelRow, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { OrderMultiSelectErrorModal } from "../OrderMultiSelectErrorModal";
import { PlanningTableRowActionProps, PlanningTableRowAction } from "./PlanningTableRowAction";

interface OrderHoldActionProps extends PlanningTableRowActionProps {
    isOnHold: boolean;
    showResults?: boolean;
}

export class OrderHoldAction extends PlanningTableRowAction {

    override validateSelection(selectedRows: ModelRow[]): boolean {
        const isOnHold = selectedRows[0].getBoolean("hold");
        const visible = selectedRows.every(row =>
            isOnHold == row.getBoolean("hold")
            && !(row.get("credit_override_status") == "P" && row.getBoolean("hold"))
            && row.isNull("manifest_id")
        );
        const isEditPermissionValid = this.validatePermissions(selectedRows, false)
            && this.validateMultiSelect(selectedRows, "max_hold_selections", ActionPermission.ACTION_MULTISELECT_HOLD)

        return visible && isEditPermissionValid;
    }

    override async execute(props: OrderHoldActionProps): Promise<void> {
        const { selectedRows, isOnHold, showResults } = props;

        if (isOnHold) {
            await this.updateSelectedOrderHoldStatus(selectedRows, false, "", showResults);
        } else {
            CommonDialogs.showInputDialog(
                `Enter the reason for placing order${selectedRows.length > 1 ? "s" : ""} on hold`,
                "On Hold Reason",
                {
                    required: true,
                    field: "hold_reason",
                    dataSource: new DataSource({ id: "ordersDataSource", url: "lme/dispatch/orders" }),
                }, {
                    okCaption: selectedRows.length > 1 ? "Apply Holds" : "Apply Hold",
                    panelTitleProps: { backgroundColor: "primary.darker", color: "primary.reverse" },
                    okProps: { backgroundColor: "primary", color: "primary.reverse", width: 110 },
                }
            ).then(async (reason) => {
                if (reason != null)
                    await this.updateSelectedOrderHoldStatus(selectedRows, true, reason, showResults)
            });
        }
    }

    private updateSelectedOrderHoldStatus(selectedRows: TableRow[], onHold: boolean, holdReason: string, showResults: boolean = true): Promise<void> {
        const inProgressSnackbar = this.showInProgressSnackbar(`Updating hold status for ${selectedRows.length} order(s)...`);

        return Api.post("lme/dispatch/order-holds/batch", {
            "keys": selectedRows.map(row => row.data.getString("order_id", null)).filter(id => !!id).map(id => ({ order_id: id })),
            "data": {
                "hold": onHold,
                "hold_reason": holdReason
            }
        }).then((result) => {
            if (!showResults) return;

            const updated = result.data.filter(result => StringUtil.isEmptyString(result.message));

            const failedResults = result.data.filter(result => !StringUtil.isEmptyString(result.message) && result.retry);
            const failedRows = failedResults.map(result => {
                const row = selectedRows.find(row => row.data.getString("order_id") === result.keys.order_id)
                row.data.set("error", result.message);
                return row;
            });

            const erroredResults = result.data.filter(result => !StringUtil.isEmptyString(result.message) && !result.retry);
            const erroredRows = erroredResults.map(result => {
                const row = selectedRows.find(row => row.data.getString("order_id") === result.keys.order_id)
                const modelRow = row.data.createBasicCopy();
                modelRow.set("error", result.message);
                return modelRow;
            });

            const retryAction = (selectedErrorRows: TableRow[]) => this.updateSelectedOrderHoldStatus(selectedErrorRows, onHold, holdReason, showResults);
            OrderMultiSelectErrorModal.showModal(failedRows, erroredRows, updated.length, "On Hold Status", "Retry", retryAction, undefined, true);
        }).finally(() => {
            if (selectedRows.length > 1) {
                selectedRows[0].table.selectedRows = [];
            }
            inProgressSnackbar.dismiss()
        });
    }

}
